/**
 * Impersonation Zustand Store
 *
 * Manages impersonation state for super admins:
 * - Calls the impersonate API to get a short-lived JWT
 * - Opens the user-tenant app in a new tab with the token
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type { StartImpersonationDto } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface ImpersonationError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface ImpersonationState {
  isStarting: boolean;
  error: ImpersonationError | null;

  startImpersonation: (accountId: string, dto: StartImpersonationDto) => Promise<boolean>;
  clearError: () => void;
}

// ============================================
// Store
// ============================================

export const useImpersonationStore = create<ImpersonationState>((set) => ({
  isStarting: false,
  error: null,

  startImpersonation: async (accountId: string, dto: StartImpersonationDto): Promise<boolean> => {
    set({ isStarting: true, error: null });

    try {
      const api = getApiClient();

      const response = await api.post<{
        token: string;
        tenantId: string;
        tenantName: string;
        userId: string;
        userEmail: string;
        expiresAt: string;
      }>(API_ENDPOINTS.SUPER_ADMIN.ACCOUNTS.IMPERSONATE(accountId), dto);

      const { token, tenantName, expiresAt } = response;

      // Open the user-tenant app in a new tab, passing the token via URL params.
      // The /impersonate route in user-tenant will store the token and redirect to dashboard.
      const env = (import.meta as any).env ?? {};
      const isProd = env.PROD === true || env.MODE === 'production';
      const userTenantUrl = isProd
        ? (env.VITE_USER_TENANT_URL_PROD || env.VITE_USER_TENANT_URL || 'https://app.respectly.ai')
        : (env.VITE_USER_TENANT_URL || 'http://localhost:5174');
      const params = new URLSearchParams({
        token,
        tenant: tenantName,
        expires: expiresAt,
      });
      window.open(`${userTenantUrl}/impersonate?${params.toString()}`, '_blank');

      set({ isStarting: false });
      return true;
    } catch (error) {
      console.error('Error starting impersonation:', error);
      set({
        isStarting: false,
        error: {
          code: 'START_IMPERSONATION_FAILED',
          message:
            error instanceof Error ? error.message : 'Failed to start impersonation session',
        },
      });
      return false;
    }
  },

  clearError: () => set({ error: null }),
}));

// ============================================
// Selector Hooks
// ============================================

/** Check if impersonation is starting */
export const useIsImpersonationStarting = () =>
  useImpersonationStore((state) => state.isStarting);
